import { useState } from "react";
import { Button, Form, Input, Modal, Typography } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import { authService } from "../../services/authService";
import styles from "./settings.module.scss";

const { Title, Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  user: any;
}

const ChangePasswordModal: React.FC<Props> = ({ open, onClose, user }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form] = Form.useForm();

  const [isPasswordTouched, setIsPasswordTouched] = useState(false);
  const [passwordRules, setPasswordRules] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
    special: false,
  });
  const [passwordMatch, setPasswordMatch] = useState<boolean | null>(null);

  const handlePasswordChange = (value: string) => {
    setIsPasswordTouched(true);
    setPasswordRules({
      length: value.length >= 6,
      lowercase: /[a-z]/.test(value),
      uppercase: /[A-Z]/.test(value),
      number: /\d/.test(value),
      special: /[@$!%*?&]/.test(value),
    });
    const confirmPassword = form.getFieldValue("confirmPassword");
    if (confirmPassword) setPasswordMatch(value === confirmPassword);
  };

  const handleConfirmPasswordChange = (value: string) => {
    const newPassword = form.getFieldValue("newPassword");
    setPasswordMatch(value ? value === newPassword : null);
  };

  const getIcon = (rule: boolean) => {
    if (!isPasswordTouched) return faXmark;
    return rule ? faCheck : faXmark;
  };

  const getRuleClass = (rule: boolean) => {
    if (!isPasswordTouched) return styles.ruleDefault;
    return rule ? styles.ruleValid : styles.ruleInvalid;
  };

  const handleClose = () => {
    setError(null);
    setIsPasswordTouched(false);
    setPasswordRules({ length: false, lowercase: false, uppercase: false, number: false, special: false });
    setPasswordMatch(null);
    form.resetFields();
    onClose();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await authService.changePassword({
        id: user.id,
        newPassword: form.getFieldValue("newPassword"),
      });
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "Failed to change password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Set your password"
      open={open}
      onCancel={handleClose}
      confirmLoading={loading}
      width={430}
      footer={null}
      destroyOnClose
    >
      <Text type="secondary" className={styles.modalSubtitle}>
        Welcome. Before you get started, create a personal password for your account.
      </Text>
      {error && <Text className={styles.modalError}>{error}</Text>}
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Title level={5} className={styles.modalInputTitle}>New Password</Title>
        <Form.Item
          name="newPassword"
          className={styles.formItemNoMargin}
          help={null}
          validateTrigger="onChange"
          rules={[{
            validator: (_, value) => {
              if (!value) return Promise.reject();
              const isValid =
                value.length >= 6 &&
                /[a-z]/.test(value) &&
                /[A-Z]/.test(value) &&
                /\d/.test(value) &&
                /[@$!%*?&]/.test(value);
              return isValid ? Promise.resolve() : Promise.reject();
            },
          }]}
        >
          <Input.Password
            placeholder="Enter new password"
            onChange={(e) => handlePasswordChange(e.target.value)}
            status={
              isPasswordTouched &&
              !(passwordRules.length && passwordRules.lowercase && passwordRules.uppercase && passwordRules.number && passwordRules.special)
                ? "error"
                : ""
            }
          />
        </Form.Item>

        <div className={styles.passwordRules}>
          <div className={styles.fullRow}>
            <span className={getRuleClass(passwordRules.length)}>
              <FontAwesomeIcon icon={getIcon(passwordRules.length)} />
            </span>
            <span>6 characters minimum</span>
          </div>
          <div className={styles.gridRow}>
            <div>
              <span className={getRuleClass(passwordRules.lowercase)}>
                <FontAwesomeIcon icon={getIcon(passwordRules.lowercase)} />
              </span>
              <span>Lowercase letter</span>
            </div>
            <div>
              <span className={getRuleClass(passwordRules.uppercase)}>
                <FontAwesomeIcon icon={getIcon(passwordRules.uppercase)} />
              </span>
              <span>Uppercase letter</span>
            </div>
            <div>
              <span className={getRuleClass(passwordRules.number)}>
                <FontAwesomeIcon icon={getIcon(passwordRules.number)} />
              </span>
              <span>Number</span>
            </div>
            <div>
              <span className={getRuleClass(passwordRules.special)}>
                <FontAwesomeIcon icon={getIcon(passwordRules.special)} />
              </span>
              <span>Special character</span>
            </div>
          </div>
        </div>

        <Title level={5} className={`${styles.modalInputTitle} ${styles.modalInputTitleTop}`}>Confirm New Password</Title>
        <Form.Item
          name="confirmPassword"
          dependencies={["newPassword"]}
          className={styles.formItemNoMargin}
          help={null}
          validateTrigger="onChange"
          rules={[{
            validator(_, value) {
              if (!value) return Promise.reject();
              return value === form.getFieldValue("newPassword") ? Promise.resolve() : Promise.reject();
            },
          }]}
        >
          <Input.Password
            placeholder="Confirm new password"
            onChange={(e) => handleConfirmPasswordChange(e.target.value)}
            status={passwordMatch === false ? "error" : ""}
          />
        </Form.Item>
        <div className={styles.passwordMatch}>
          {passwordMatch === null ? null : passwordMatch ? (
            <p className={styles.ruleValid}><FontAwesomeIcon icon={faCheck} /> Passwords match</p>
          ) : (
            <p className={styles.ruleInvalid}><FontAwesomeIcon icon={faXmark} /> Passwords do not match</p>
          )}
        </div>

        <Form.Item shouldUpdate className={styles.submitFormItem}>
          {() => {
            const hasErrors = form.getFieldsError().some(({ errors }) => errors.length);
            const isTouched = form.isFieldsTouched(true);
            return (
              <Button
                type="primary"
                block
                htmlType="submit"
                loading={loading}
                disabled={!isTouched || hasErrors}
                className={styles.submitButton}
              >
                Set Password and Continue
              </Button>
            );
          }}
        </Form.Item>
        <Text type="secondary" className={styles.modalFootnote}>
          This will only update your password for XD API and will not affect any other Xpressdocs affiliated accounts.
        </Text>
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
